import { useMemo } from 'react'

import {
  BufferTrackerReact as BufferTracker,
  Client,
} from '@buffer-mono/tracking-plan'

import { useOrganizationId } from '~publish/contexts/AccountContext'
import { useCommonTrackingProperties } from '~publish/hooks/useCommonTrackingProperties'

import type { TakeawayFeedback } from '../graphqlEnums'

import type { TakeawayCtaTrackingContext } from './useTrackTakeawayCtaClicked'

/** Per-card context plus the feedback value set by the click. */
export type TakeawayFeedbackClickProperties = TakeawayCtaTrackingContext & {
  /** Feedback set by the interaction; null when the user cleared a selection. */
  feedback: TakeawayFeedback | null
}

type TrackTakeawayFeedbackClicked = (
  properties: TakeawayFeedbackClickProperties,
) => void

/**
 * Returns a `Takeaway Feedback Clicked` tracker, called from the thumbs up /
 * thumbs down handlers with the same per-card context the CTA tracker uses
 * (see {@link TakeawayCtaTrackingContext}) plus the resulting feedback value.
 *
 * Mirrors {@link useTrackTakeawayCtaClicked}: fires `BufferTracker` directly
 * with the common tracking props. Unlike the once-per-view `Takeaways Viewed`
 * impression, this fires on every interaction, so there is no dedupe guard.
 *
 * Only persisted cards render feedback controls (the `takeaway.id` guard in
 * `TakeawayCard.tsx`), so synthetic frontend-only cards such as the LinkedIn boost
 * card never reach this; the `takeawayId`/`channelId` guard is a belt-and-braces
 * fallback for the nullable context fields.
 */
export function useTrackTakeawayFeedbackClicked(): TrackTakeawayFeedbackClicked {
  const organizationId = useOrganizationId()
  const commonTrackingProperties = useCommonTrackingProperties()

  return useMemo(
    () =>
      ({
        takeawayId,
        channelId,
        serviceType,
        category,
        cardPosition,
        feedback,
      }: TakeawayFeedbackClickProperties): void => {
        if (!organizationId || !takeawayId || !channelId) return
        BufferTracker.takeawayFeedbackClicked({
          ...commonTrackingProperties,
          clientName: Client.PublishWeb,
          organizationId,
          takeawayId,
          takeawayCategory: category,
          takeawayPosition: cardPosition,
          takeawayFeedback: feedback,
          channelId,
          serviceType,
        })
      },
    [organizationId, commonTrackingProperties],
  )
}
